import { dehydrate, hydrate, type QueryClient } from "@tanstack/react-query";

// Persist the TanStack Query cache to localStorage so the app can render
// last-known data on a cold start / offline. Uses dehydrate/hydrate (shipped in
// @tanstack/react-query) — no extra dependency. Only successful queries are
// dehydrated by default. Best-effort throughout: any storage/parse failure is
// swallowed so it can never break app startup.

// v2: RosterUser gained a required `characters` array (splits-and-characters);
// v1 snapshots lack it and would crash consumers, so discard them on upgrade.
const KEY = "laget:query-cache:v2";
const MAX_AGE = 1000 * 60 * 60 * 24; // 24h — older snapshots are discarded

type Snapshot = { t: number; state: ReturnType<typeof dehydrate> };

// Hydrate a previously persisted cache. Call before the first render so the
// hydrated data is available to the initial route load.
export function restoreQueryCache(client: QueryClient): void {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return;
    const snapshot = JSON.parse(raw) as Snapshot;
    if (!snapshot.t || Date.now() - snapshot.t > MAX_AGE) {
      localStorage.removeItem(KEY);
      return;
    }
    hydrate(client, snapshot.state);
  } catch {
    localStorage.removeItem(KEY);
  }
}

// Write the cache to storage on every change, debounced.
export function persistQueryCache(client: QueryClient): void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  client.getQueryCache().subscribe(() => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      try {
        const snapshot: Snapshot = { t: Date.now(), state: dehydrate(client) };
        localStorage.setItem(KEY, JSON.stringify(snapshot));
      } catch {
        // ignore quota / private-mode failures
      }
    }, 1000);
  });
}
